import { useGetAuctionForms } from "apollo/hooks/queries";
import { composeProgress, isLoading } from "apollo/utils";
import {
  FILE_SCOPE,
  isRehabFormType,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { getPatientName } from "core/model/patients";
import {
  Auction,
  AuctionForm,
  DocsFormName,
  FileParams,
  FileState,
  Patient,
  QueryProgress,
  RehabForms,
  TransitionalCare,
  ValueOf,
} from "core/types";
import {
  Button,
  Form,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
} from "ds/ui";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useGetFilesFeatures } from "dsl/ecosystems/PatientFiles/useGetFilesFeatures";
import { getFileNameAndIsNewVersionAvailable } from "dsl/ecosystems/PatientFiles/utils";
import {
  IMPORT_AUCTION_ID,
  REIMPORT_AUCTION_ID,
} from "dsl/ecosystems/PatientForms/RehabForms/RehabFormWrapper";
import { convertAuctionFormsToRehabForms } from "dsl/ecosystems/PatientForms/RehabForms/utils";
import { findTransitionalCareForm } from "dsl/ecosystems/PatientForms/TransitionalCare";
import { isFileV2 } from "dsl/organisms/FileTables";
import { RefreshCwIcon, SparklesIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslations } from "translations";
import { useRecareDocsContext } from "../context";
import { FormFooter } from "./Footer";
import { DocsFormFields } from "./FormFields";
import { HEADER_BANNERS, HeaderBannerAction } from "./HeaderBanners";
import { DocsModalSources } from "./Sources";
import { useDocsForm } from "./useDocsForm";

export const DOCS_FORM_FOOTER_SCREENS = {
  DEFAULT: "default",
  DELETE_CONFIRMATION: "delete-confirmation",
  SAVE_CONFIRMATION: "save-confirmation",
  WARN_OPEN_PDF: "warn-open-pdf",
  WARN_SEND_TO_HIS: "warn-send-to-his",
} as const;

export type DocsFormFooterScreen = ValueOf<typeof DOCS_FORM_FOOTER_SCREENS>;

const useFetchFiles = ({
  auction,
  patient,
}: {
  auction: Auction;
  patient: Patient;
}) => {
  const [filesProgress, setFilesProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [files, setFiles] = useState<{ name: string; file: FileState }[]>([]);
  const toast = useToast();
  const translations = useTranslations();

  const params: FileParams = `auction_id=${auction.id}`;

  const { getFilesHISProgress, queryProgress, uploadedFilesV2 } =
    useGetFilesFeatures({
      action: undefined,
      externalId: patient.external_id,
      params,
    });

  const filteredUploadedFilesV2 = useMemo(
    () =>
      uploadedFilesV2?.filter((file) =>
        isFileV2(file) ? file.file_scope !== FILE_SCOPE.ELIGIBILITY : true,
      ),
    [uploadedFilesV2],
  );

  useEffect(() => {
    (async () => {
      try {
        setFilesProgress(QUERY_PROGRESS_PENDING);
        const filesData = await Promise.all(
          filteredUploadedFilesV2?.map(async (file) => {
            const props = await getFileNameAndIsNewVersionAvailable({
              canAccessPatientData: true,
              file,
            });

            if (props.shouldBlur) {
              return null;
            }

            if (typeof props.value === "string" && file.updated_at) {
              return { file, name: props.value };
            }

            return null;
          }) ?? [],
        );
        if (filesData.truthy()) {
          setFiles(filesData.truthy());
        }
        setFilesProgress(QUERY_PROGRESS_SUCCEED);
      } catch (error) {
        toast({
          color: "danger",
          message: translations.general.error,
        });
        console.error("Error fetching files:", error);
        setFilesProgress(QUERY_PROGRESS_FAILED);
      }
    })();
  }, [filteredUploadedFilesV2]);

  return {
    files,
    filesProgress: composeProgress([
      filesProgress,
      getFilesHISProgress,
      queryProgress,
    ]),
    isLoadingFiles: isLoading(filesProgress),
  };
};

const DocsModalHeader = ({
  hasNoDocumentsExtracted,
  hasPreviousFormValue,
  header,
  headerBannerAction,
  importFormData,
  importFormProgress,
  onClose,
  patient,
}: {
  hasNoDocumentsExtracted: boolean;
  hasPreviousFormValue: boolean;
  header: string;
  headerBannerAction: HeaderBannerAction;
  importFormData: () => Promise<void>;
  importFormProgress: QueryProgress;
  onClose: () => void;
  patient: Patient;
}) => {
  const translations = useTranslations();
  const { isDocsAndExtractActive } = useIsDocsAndExtractActive();
  const isFormFilled =
    hasPreviousFormValue || importFormProgress === QUERY_PROGRESS_SUCCEED;

  return (
    <ModalHeader className="flex w-full items-center justify-between">
      <div className="flex flex-col gap-1">
        <p className="text-body-md font-semibold text-gray-600">{`${getPatientName(
          patient,
        )} (${patient.user_id})`}</p>
        <h1 className="text-heading-lg font-bold">{header}</h1>
      </div>
      <div className="flex gap-4">
        <Button
          color="primary"
          id={isFormFilled ? REIMPORT_AUCTION_ID : IMPORT_AUCTION_ID}
          isDisabled={
            isDocsAndExtractActive &&
            (headerBannerAction.banner === HEADER_BANNERS.EXTRACTING ||
              hasNoDocumentsExtracted)
          }
          isLoading={isLoading(importFormProgress)}
          size="sm"
          startContent={isFormFilled ? <RefreshCwIcon /> : <SparklesIcon />}
          variant={isFormFilled ? "ghost" : "solid"}
          onPress={() => {
            importFormData();
          }}
        >
          {isFormFilled
            ? translations.extractDocs.splitView.header.buttonRefreshData
            : translations.extractDocs.splitView.header.buttonImportData}
        </Button>
        <Button
          color="primary"
          endContent={<XIcon />}
          id="close-modal"
          size="sm"
          variant="light"
          onPress={onClose}
        >
          {translations.extractDocs.splitView.header.buttonClose}
        </Button>
      </div>
    </ModalHeader>
  );
};

const DocsModalContent = ({
  auction,
  dischargeLetterForms,
  files,
  formName,
  patient,
  rehabForms,
  transitionalCare,
}: {
  formName: DocsFormName;
  auction: Auction;
  patient: Patient;
  files: { name: string; file: FileState }[];
  rehabForms: RehabForms | undefined;
  transitionalCare: TransitionalCare | AuctionForm | undefined;
  dischargeLetterForms: AuctionForm[];
}) => {
  const translations = useTranslations();

  const {
    config,
    confirmationChecked,
    footerBanner,
    footerScreen,
    form,
    handleBack,
    handleDelete,
    handleDeleteProgress,
    handleOpenPdf,
    handleSendToHis,
    handleSubmit,
    hasPreviousFormValue,
    headerBannerAction,
    importFormData,
    importFormProgress,
    methods,
    noDocumentsExtracted,
    onCloseHeaderBanner,
    onSubmitProgress,
    progressOpenPdf,
    progressSendToHis,
    setConfirmationChecked,
    setFooterBanner,
    setFooterScreen,
    setHeaderBannerAction,
    skipRequiredValidation,
    sourceInformation,
  } = useDocsForm({
    auction,
    dischargeLetterForms,
    formName,
    patient,
    rehabForms,
    transitionalCare,
  });
  const { setFormModal } = useRecareDocsContext();

  return (
    <FormProvider {...methods}>
      <Form className="flex h-full gap-0" onSubmit={handleSubmit}>
        <DocsModalHeader
          hasNoDocumentsExtracted={noDocumentsExtracted || files.length === 0}
          hasPreviousFormValue={hasPreviousFormValue}
          header={config.header({ translations })}
          headerBannerAction={headerBannerAction}
          importFormData={importFormData}
          importFormProgress={importFormProgress}
          patient={patient}
          onClose={() => {
            if (
              methods.formState.isDirty &&
              headerBannerAction.banner !== HEADER_BANNERS.FORM_DIRTY_WARNING
            ) {
              setHeaderBannerAction({
                banner: HEADER_BANNERS.FORM_DIRTY_WARNING,
              });
            } else {
              setFormModal(null);
            }
          }}
        />
        <hr className="w-full border-gray-400" />
        <ModalBody className="flex h-full w-full flex-row gap-0 overflow-hidden p-0">
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 overflow-y-auto">
              <DocsFormFields<typeof formName>
                auction={auction}
                config={config}
                formName={formName}
                headerBannerAction={headerBannerAction}
                importData={importFormData}
                shouldSkipRequiredValidation={skipRequiredValidation}
                sourceInformation={sourceInformation}
                onCloseHeaderBanner={onCloseHeaderBanner}
              />
            </div>
            <FormFooter
              canPrint={config.config.canPrint}
              canSendToHis={config.config.canSendToHis}
              footerBanner={footerBanner}
              footerScreen={footerScreen}
              form={form as AuctionForm | undefined}
              handleOpenPdf={handleOpenPdf}
              handleSendToHis={handleSendToHis}
              hasPreviousFormValue={hasPreviousFormValue}
              isConfirmationChecked={confirmationChecked}
              progressOpenPdf={progressOpenPdf}
              progressSendToHis={progressSendToHis}
              setConfirmationChecked={setConfirmationChecked}
              setFooterBanner={setFooterBanner}
              setFooterScreen={setFooterScreen}
              onBack={handleBack}
              onDelete={handleDelete}
              onDeleteProgress={handleDeleteProgress}
              onSubmitProgress={onSubmitProgress}
            />
          </div>
          <DocsModalSources
            files={files}
            patient={patient}
            resetSelectedSource={sourceInformation.resetSelectedSource}
            sourceDocuments={
              sourceInformation.selectedSource?.sourceFields.source_documents ??
              []
            }
          />
        </ModalBody>
      </Form>
    </FormProvider>
  );
};

export const RecareDocsModal = ({
  auction,
  patient,
}: {
  auction: Auction;
  patient: Patient;
}) => {
  const { formModal } = useRecareDocsContext();

  const [auctionFormsQueryProgress, auctionForms] = useGetAuctionForms({
    auctionId: auction.id,
    skip: formModal === null,
  });

  const rehabForms = convertAuctionFormsToRehabForms(
    auctionForms?.filter((form) => isRehabFormType(form.form_type)),
  );

  const transitionalCare = findTransitionalCareForm(auctionForms);

  const { files, filesProgress } = useFetchFiles({
    auction,
    patient,
  });

  if (!formModal) return null;

  return (
    <Modal
      classNames={{
        backdrop: "!z-modal",
        base: "max-h-[95vh] overflow-y-auto",
        wrapper: "!z-modal",
      }}
      hideCloseButton
      isDismissable={false}
      isOpen
    >
      <ModalContent className="flex h-[95vh] w-full min-w-[95vw] flex-col">
        {isLoading([auctionFormsQueryProgress, filesProgress]) ? (
          <ModalBody className="flex h-full w-full items-center justify-center">
            <Spinner color="primary" size="lg" testId="spinner-loading-docs" />
          </ModalBody>
        ) : (
          <DocsModalContent
            auction={auction}
            dischargeLetterForms={auctionForms ?? []}
            files={files}
            formName={formModal}
            patient={patient}
            rehabForms={rehabForms}
            transitionalCare={transitionalCare}
          />
        )}
      </ModalContent>
    </Modal>
  );
};
